import { api, redirectToLogin, isRedirectingToLogin } from '@/services/api';
import { getAccessToken, getRefreshToken, storeTokens, removeTokens, isTokenExpired, refreshTokenAPI } from '@/services/token';
import { ApiResponse } from '@/types/api';

// リフレッシュ処理の重複排除用Promise
let refreshPromise: Promise<boolean> | null = null;

/**
 * ログイン処理
 * - 認証情報をバックエンドに送信
 * - トークンを取得・保存
 */
export const login = async (username: string, password: string): Promise<ApiResponse> => {
  const response = await api.post('/login/', { username, password });
  const { access, refresh } = response.data;
  storeTokens(access, refresh);
  return response.data;
};

/**
 * ログアウト処理
 * - トークンを削除
 */
export const logout = (): void => {
  removeTokens();
};

/**
 * アクセストークンの検証
 * @returns トークンが有効な場合はtrue、無効な場合はfalse
 */
export const validateToken = (): boolean => {
  try {
    const token = getAccessToken();
    if (!token) return false;

    // token.tsのisTokenExpiredを使用して一貫性を保つ
    return !isTokenExpired(token);
  } catch {
    return false;
  }
};

/**
 * トークンの検証と必要に応じた更新を行う
 * @returns 有効なトークンが存在する（または更新できた）場合はtrue
 */
export const validateAndRefreshToken = async (): Promise<boolean> => {
  try {
    // 現在のトークンが有効な場合はtrueを返す
    if (validateToken()) {
      return true;
    }

    // トークンが無効な場合は更新を試みる
    return await refreshTokens();
  } catch {
    return false;
  }
};

/**
 * トークンの更新処理
 *
 * 重複排除: 同時に複数箇所（checkAuth, API interceptor等）から呼ばれても
 * 実際のAPIリクエストは1つだけ実行される。
 * ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION 環境での競合を防止。
 *
 * @param shouldRedirect リフレッシュ失敗時にログイン画面へリダイレクトするか（デフォルト: true）
 */
export const refreshTokens = async (shouldRedirect: boolean = true): Promise<boolean> => {
  // 既にリフレッシュ処理が実行中の場合は同じPromiseを返す
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        if (shouldRedirect && !getAccessToken()) {
          redirectToLogin();
        }
        return false;
      }

      if (isTokenExpired(refreshToken)) {
        console.debug('リフレッシュトークンが期限切れです');
        removeTokens();
        if (shouldRedirect) {
          redirectToLogin();
        }
        return false;
      }

      const { access, refresh } = await refreshTokenAPI(refreshToken);

      // リダイレクト処理が開始されている場合は新トークンを保存しない
      // （別の経路でリフレッシュ失敗→リダイレクト→その後に本リクエストが成功するケースを防止）
      if (isRedirectingToLogin()) {
        console.debug('リダイレクト中のためトークン保存をスキップ');
        return false;
      }

      storeTokens(access, refresh);
      console.debug('トークンの更新に成功しました');
      return true;
    } catch (error) {
      if (error instanceof Error && error.message.includes('401')) {
        console.debug('認証エラー: リフレッシュトークンが無効です');
      } else if (error instanceof Error) {
        console.error('トークン更新エラー:', error.message);
      }
      removeTokens();
      if (shouldRedirect) {
        redirectToLogin();
      }
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};
